import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Container, Row, Col, Button, Spinner } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { FaStar, FaRegStar } from 'react-icons/fa';
import styles from './bodywon.module.css';
import { useSettings } from '../context/SettingsContext';
import { fetchProductById } from '../utils/productStorefront';
import { fetchBundlesForProduct } from '../utils/bundleStorefront';
import ProductReviewStars from './ProductReviewStars';
import FrequentlyBoughtTogether from './FrequentlyBoughtTogether';
import { fetchProductReviewsForProduct } from '../utils/productReviewApi';
import { formatReviewDate, normalizeProductReview } from '../utils/productReviewHelpers';

const LENS_SIZE = 120;
const ZOOM_SCALE = 2.4;

const ProductDetailPage = ({ onAddToCart, onAddBundleToCart }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { formatPrice } = useSettings();
  const stageRef = useRef(null);

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [showZoom, setShowZoom] = useState(false);
  const [lensPos, setLensPos] = useState({ x: 0, y: 0 });
  const [zoomPos, setZoomPos] = useState({ x: 0, y: 0 });
  const [stageSize, setStageSize] = useState({ width: 0, height: 0 });
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [productBundles, setProductBundles] = useState([]);

  const loadReviews = useCallback(async (productId) => {
    if (!productId) return;
    setReviewsLoading(true);
    try {
      const { items } = await fetchProductReviewsForProduct(productId);
      const normalized = items.map(normalizeProductReview);
      setReviews(normalized);
    } catch {
      setReviews([]);
    } finally {
      setReviewsLoading(false);
    }
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setNotFound(false);
    setActiveImageIndex(0);
    setSelectedSize('');
    setSelectedColor('');
    setShowZoom(false);
    setProductBundles([]);

    fetchProductById(id)
      .then((result) => {
        if (!active) return;
        if (!result) {
          setNotFound(true);
          setProduct(null);
        } else {
          setProduct(result);
          setSelectedSize(result.sizes?.[0] || '');
          setSelectedColor(result.colors?.[0]?.label || '');
          loadReviews(result.id);
          fetchBundlesForProduct(result.id).then((bundles) => {
            if (active) setProductBundles(bundles);
          });
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [id, loadReviews]);

  const images = product?.images?.length
    ? product.images
    : product?.imageSrc
      ? [{ id: 0, url: product.imageSrc, isPrimary: true }]
      : [];

  const activeImage = images[activeImageIndex]?.url || product?.imageSrc || '';

  useEffect(() => {
    const updateStageSize = () => {
      if (!stageRef.current) return;
      setStageSize({
        width: stageRef.current.clientWidth,
        height: stageRef.current.clientHeight,
      });
    };
    updateStageSize();
    window.addEventListener('resize', updateStageSize);
    return () => window.removeEventListener('resize', updateStageSize);
  }, [activeImage, loading]);

  const handleMouseMove = (e) => {
    if (!stageRef.current || window.innerWidth < 992) return;
    const rect = stageRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const maxX = rect.width - LENS_SIZE;
    const maxY = rect.height - LENS_SIZE;
    const lensX = Math.max(0, Math.min(x - LENS_SIZE / 2, maxX));
    const lensY = Math.max(0, Math.min(y - LENS_SIZE / 2, maxY));
    setLensPos({ x: lensX, y: lensY });
    setZoomPos({
      x: lensX * ZOOM_SCALE,
      y: lensY * ZOOM_SCALE,
    });
    setShowZoom(true);
  };

  const handleAddToCart = () => {
    if (product && onAddToCart) {
      onAddToCart(product);
    }
  };

  if (loading) {
    return (
      <section className={styles.productDetailContainer}>
        <Container className="text-center py-5">
          <Spinner animation="border" role="status" />
        </Container>
      </section>
    );
  }

  if (notFound || !product) {
    return (
      <section className={styles.productDetailContainer}>
        <Container className="text-center py-5">
          <p className="mb-3">Product not found.</p>
          <Button variant="outline-dark" onClick={() => navigate('/')}>
            Back to Home
          </Button>
        </Container>
      </section>
    );
  }

  return (
    <section className={styles.productDetailContainer}>
      <Container>
        <Row className={`align-items-start g-4 g-lg-5 ${styles.productDetailRow}`}>
          <Col lg={6} xl={6}>
            <div className={`${styles.productGalleryWrap}${images.length > 1 ? '' : ` ${styles.productGalleryWrapSingle}`}`}>
              {images.length > 1 ? (
                <div className={styles.productGalleryThumbs} aria-label="Product images">
                  {images.map((img, index) => (
                    <div
                      key={img.id ?? index}
                      role="presentation"
                      className={`${styles.productGalleryThumb}${activeImageIndex === index ? ` ${styles.productGalleryThumbActive}` : ''}`}
                      onMouseEnter={() => {
                        setActiveImageIndex(index);
                        setShowZoom(false);
                      }}
                    >
                      <img src={img.url} alt="" draggable={false} />
                    </div>
                  ))}
                </div>
              ) : null}

              <div className={styles.productGalleryMain}>
                <div
                  ref={stageRef}
                  className={styles.productGalleryStage}
                  onMouseMove={handleMouseMove}
                  onMouseLeave={() => setShowZoom(false)}
                >
                  {activeImage ? (
                    <img
                      key={activeImage}
                      src={activeImage}
                      alt={product.name}
                      className={styles.productGalleryStageImg}
                    />
                  ) : (
                    <div className={styles.productGalleryPlaceholder} />
                  )}
                  {showZoom && stageSize.width > 0 ? (
                    <div
                      className={styles.productZoomLens}
                      style={{
                        width: LENS_SIZE,
                        height: LENS_SIZE,
                        transform: `translate(${lensPos.x}px, ${lensPos.y}px)`,
                      }}
                    />
                  ) : null}
                </div>
              </div>
            </div>
          </Col>

          <Col lg={6} xl={6}>
            <div className={styles.productInfoPanel}>
              {showZoom && activeImage && stageSize.width > 0 ? (
                <div className={styles.productZoomOverlay} aria-hidden="true">
                  <div
                    className={styles.productZoomOverlayInner}
                    style={{
                      backgroundImage: `url(${activeImage})`,
                      backgroundSize: `${stageSize.width * ZOOM_SCALE}px ${stageSize.height * ZOOM_SCALE}px`,
                      backgroundPosition: `-${zoomPos.x}px -${zoomPos.y}px`,
                    }}
                  />
                </div>
              ) : null}

            <h1 className={styles.productTitle}>{product.name}</h1>

            <div className={styles.ratingAndReviews}>
              <ProductReviewStars
                product={product}
                variant="detail"
                showWriteLink
                onSeeReviews={() => {
                  document.getElementById('customer-reviews')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                onReviewSubmitted={(_summary, items) => {
                  setReviews((items ?? []).map(normalizeProductReview));
                }}
              />
            </div>

            <div className={styles.productPrice}>
              {product.originalPrice ? (
                <>
                  <span className={styles.originalPrice} style={{ marginRight: '0.75rem' }}>
                    {formatPrice(product.originalPrice)}
                  </span>
                  <span>{formatPrice(product.price)}</span>
                </>
              ) : (
                formatPrice(product.price)
              )}
            </div>

            {product.sku ? (
              <div className={styles.productSkuRow}>
                <span className={styles.productSkuLabel}>SKU</span>
                <span className={styles.productSkuValue}>{product.sku}</span>
              </div>
            ) : null}

            {product.colors?.length > 0 ? (
              <div className={styles.optionsGroup}>
                <label className={styles.optionLabel}>Color</label>
                <div className={styles.colorOptions}>
                  {product.colors.map((color) => (
                    <button
                      key={color.label}
                      type="button"
                      className={`${styles.colorSwatchDetail}${selectedColor === color.label ? ` ${styles.colorSwatchDetailActive}` : ''}`}
                      aria-label={color.label}
                      onMouseEnter={() => setSelectedColor(color.label)}
                      onClick={() => setSelectedColor(color.label)}
                    >
                      <span style={{ backgroundColor: color.value }} />
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            {product.sizes?.length > 0 ? (
              <div className={styles.optionsGroup}>
                <label className={styles.optionLabel}>
                  Size
                  {selectedSize ? <span className={styles.optionSelected}>{selectedSize}</span> : null}
                </label>
                <div className={styles.sizeOptions}>
                  {product.sizes.map((size) => (
                    <button
                      key={size}
                      type="button"
                      className={selectedSize === size ? styles.sizeOptionActive : ''}
                      onMouseEnter={() => setSelectedSize(size)}
                      onClick={() => setSelectedSize(size)}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            <div className={styles.productDetailActions}>
              <Button className={styles.addToCartBtn} onClick={handleAddToCart}>
                Add to Cart
              </Button>
            </div>
            </div>
          </Col>
        </Row>

        <FrequentlyBoughtTogether
          bundles={productBundles}
          currentProductId={product.id}
          onAddBundle={onAddBundleToCart}
          formatPrice={formatPrice}
        />

        <section className={styles.reviewsSection} id="customer-reviews">
          <h2 className={styles.reviewsTitle}>Customer Reviews</h2>
          {reviewsLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" role="status" />
            </div>
          ) : reviews.length === 0 ? (
            <p className="text-muted mb-0">No reviews yet. Click the stars above to be the first!</p>
          ) : (
            reviews.map((review) => (
              <article key={review.id} className={styles.reviewItem}>
                <div className={styles.reviewRating}>
                  {[1, 2, 3, 4, 5].map((value) =>
                    value <= review.rating ? <FaStar key={value} /> : <FaRegStar key={value} />
                  )}
                  <span className="ms-2">{review.rating}/5</span>
                </div>
                <h3 className={styles.reviewTitle}>{review.title}</h3>
                {review.createdAt ? (
                  <p className={styles.reviewDate}>{formatReviewDate(review.createdAt)}</p>
                ) : null}
                {review.text ? (
                  <p className={styles.reviewComment}>{review.text}</p>
                ) : null}
              </article>
            ))
          )}
        </section>
      </Container>
    </section>
  );
};

export default ProductDetailPage;
